import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listConsentTemplates,
  createConsentTemplate,
  updateConsentTemplate,
  deleteConsentTemplate,
  sendConsent,
  listConsentLog,
  type ConsentTemplate,
} from "../api/consent";
import { normalizePhone } from "../lib/phone";

function readErr(e: unknown): string {
  const ax = e as { response?: { data?: { detail?: string; error?: string } }; message?: string };
  return ax.response?.data?.detail || ax.response?.data?.error || ax.message || "Something went wrong";
}

function TemplateEditor({ initial, onClose, onSaved }: {
  initial: ConsentTemplate | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [active, setActive] = useState(initial?.is_active ?? true);
  const save = useMutation({
    mutationFn: () => {
      const payload = { name, body, is_active: active };
      return initial
        ? updateConsentTemplate(initial.consent_template_id, payload)
        : createConsentTemplate(payload);
    },
    onSuccess: onSaved,
  });
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-3">
        <h2 className="text-lg font-bold">{initial ? "Edit" : "New"} consent template</h2>
        <div>
          <label htmlFor="ct-name" className="block text-xs text-gray-500 mb-1">Name</label>
          <input id="ct-name" className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard consent" />
        </div>
        <div>
          <label htmlFor="ct-body" className="block text-xs text-gray-500 mb-1">
            Message ({body.length}/1600)
          </label>
          <textarea id="ct-body" rows={4} maxLength={1600}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Reply YES to confirm you agree to receive automated calls and text messages from [Your Company]. Msg & data rates may apply. Reply STOP to opt out." />
          <p className="text-xs text-gray-400 mt-1">Include your own company name and wording. Recipients reply YES; replies are received on your existing number, not here.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active (available to send)
        </label>
        {save.isError && <p className="text-xs text-red-600">{readErr(save.error)}</p>}
        <div className="flex gap-2 pt-1">
          <button disabled={!name || !body || save.isPending} onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50">
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function ConsentPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ConsentTemplate | null | undefined>(undefined); // undefined = closed
  const [sendTo, setSendTo] = useState("");
  const [sendTid, setSendTid] = useState<number | "">("");
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const [logPage, setLogPage] = useState(1);

  const templates = useQuery({ queryKey: ["consent-templates"], queryFn: () => listConsentTemplates(true) });
  const log = useQuery({
    queryKey: ["consent-log", logPage],
    queryFn: () => listConsentLog(logPage, 50),
    placeholderData: keepPreviousData,
  });
  const del = useMutation({
    mutationFn: (id: number) => deleteConsentTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consent-templates"] }),
  });
  const send = useMutation({
    mutationFn: () => sendConsent({ template_id: Number(sendTid), to: normalizePhone(sendTo) }),
    onSuccess: (r) => { setSendMsg(`Sent to ${r.to}.`); setSendTo(""); qc.invalidateQueries({ queryKey: ["consent-log"] }); },
    onError: (e) => setSendMsg(readErr(e)),
  });

  const rows = templates.data ?? [];
  const active = rows.filter((t) => t.is_active);
  const logRows = log.data?.data ?? [];
  const logTotal = log.data?.total ?? 0;
  const logPages = Math.max(1, Math.ceil(logTotal / 50));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Consent SMS</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Pre-written consent messages your agents can text to callers during a call, sent from your platform number.
          Recipients reply to confirm; those replies come back on your number, not here. Every send is logged below.
        </p>
      </div>

      {/* templates */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Templates</h2>
          <button onClick={() => setEditing(null)} className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1.5 text-sm">New template</button>
        </div>
        {templates.isLoading ? <p className="p-4 text-sm text-gray-500">Loading…</p> :
         templates.isError ? <p className="p-4 text-sm text-red-600">{readErr(templates.error)}</p> :
         rows.length === 0 ? <p className="p-4 text-sm text-gray-500">No templates yet. Create one to let agents send consent requests.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500"><tr>
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Message</th><th className="px-4 py-2">Active</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((t) => (
                <tr key={t.consent_template_id}>
                  <td className="px-4 py-2 font-medium">{t.name}</td>
                  <td className="px-4 py-2 text-gray-600 max-w-md truncate" title={t.body}>{t.body}</td>
                  <td className="px-4 py-2">{t.is_active
                    ? <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">active</span>
                    : <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-600">off</span>}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(t)} className="text-brand hover:underline mr-3">Edit</button>
                    <button onClick={() => { if (confirm(`Delete "${t.name}"?`)) del.mutate(t.consent_template_id); }} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* manual send */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <h2 className="font-semibold text-gray-800">Send a consent message</h2>
        <div className="flex flex-wrap items-end gap-3 text-sm">
          <div>
            <label htmlFor="s-to" className="block text-xs text-gray-500 mb-1">To number</label>
            <input id="s-to" className="border border-gray-300 rounded px-3 py-2 font-mono w-44" placeholder="10-digit number" value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
          </div>
          <div>
            <label htmlFor="s-tid" className="block text-xs text-gray-500 mb-1">Template</label>
            <select id="s-tid" className="border border-gray-300 rounded px-3 py-2 min-w-[14rem]" value={sendTid} onChange={(e) => setSendTid(e.target.value === "" ? "" : Number(e.target.value))}>
              <option value="">Select…</option>
              {active.map((t) => <option key={t.consent_template_id} value={t.consent_template_id}>{t.name}</option>)}
            </select>
          </div>
          <button disabled={!sendTo || sendTid === "" || send.isPending}
            onClick={() => { setSendMsg(null); send.mutate(); }}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 disabled:opacity-50">
            {send.isPending ? "Sending…" : "Send"}
          </button>
        </div>
        {sendMsg && <p className="text-sm text-gray-700">{sendMsg}</p>}
      </div>

      {/* log */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Send log <span className="text-gray-400 font-normal">({logTotal.toLocaleString()})</span></h2>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <button disabled={logPage <= 1} onClick={() => setLogPage((p) => p - 1)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">‹</button>
            <span>page {logPage} / {logPages}</span>
            <button disabled={logPage >= logPages} onClick={() => setLogPage((p) => p + 1)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">›</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500"><tr>
              <th className="px-4 py-2">When</th><th className="px-4 py-2">To</th><th className="px-4 py-2">By</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Message</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {logRows.map((r) => (
                <tr key={r.consent_send_log_id}>
                  <td className="px-4 py-2 tabular-nums text-gray-500">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2 font-mono">{r.to_number}</td>
                  <td className="px-4 py-2 text-gray-600">{r.sent_by_kind}</td>
                  <td className="px-4 py-2">{r.status === "sent"
                    ? <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">sent</span>
                    : <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800" title={r.error ?? ""}>failed</span>}</td>
                  <td className="px-4 py-2 text-gray-600 max-w-sm truncate" title={r.body}>{r.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logRows.length === 0 && <p className="p-4 text-sm text-gray-400">No sends yet.</p>}
        </div>
      </div>

      {editing !== undefined && (
        <TemplateEditor initial={editing} onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); qc.invalidateQueries({ queryKey: ["consent-templates"] }); }} />
      )}
    </div>
  );
}
